import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Card,
  CardContent,
  CardHeader,
} from "@mui/material";
import Layout from "../components/layout/CompliceLayout";
import {
  CheckCircle as ComplianceIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

// Sample Regulatory Compliance Data
const regulatoryData = [
  {
    id: 1,
    regulation: "GDPR",
    category: "Data Protection",
    status: "Compliant",
    lastReviewed: "2024-05-15",
    complianceScore: 95,
    nextReviewDate: "2025-05-15",
    details: "Full compliance with data protection and privacy requirements.",
  },
  {
    id: 2,
    regulation: "SOX",
    category: "Financial Reporting",
    status: "Needs Attention",
    lastReviewed: "2024-03-20",
    complianceScore: 75,
    nextReviewDate: "2024-09-20",
    details: "Minor non-compliance issues in financial reporting controls.",
  },
  {
    id: 3,
    regulation: "HIPAA",
    category: "Healthcare Data",
    status: "Critical",
    lastReviewed: "2024-02-10",
    complianceScore: 60,
    nextReviewDate: "2024-08-10",
    details: "Significant compliance gaps in healthcare data protection.",
  },
];

const ComplianceStatusChip = ({ status }) => {
  const getChipProps = () => {
    switch (status) {
      case "Compliant":
        return { color: "success", icon: <ComplianceIcon /> };
      case "Needs Attention":
        return { color: "warning", icon: <WarningIcon /> };
      case "Critical":
        return { color: "error", icon: <ErrorIcon /> };
      default:
        return { color: "default" };
    }
  };

  const chipProps = getChipProps();

  return <Chip label={status} {...chipProps} variant="outlined" />;
};

const RegulatoryComplice = () => {
  const [selectedRegulation, setSelectedRegulation] = useState(null);

  const handleOpenDetails = (regulation) => {
    setSelectedRegulation(regulation);
  };

  const handleCloseDetails = () => {
    setSelectedRegulation(null);
  };
  return (
    <Layout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Regulatory Compliance Tracking
        </Typography>

        <Grid container spacing={3}>
          {/* Compliance Overview */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Compliance Summary" />
              <CardContent>
                <Typography variant="h6">Overall Compliance Status</Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
                  <Box sx={{ width: "100%", mr: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={80}
                      color="success"
                    />
                  </Box>
                  <Box sx={{ minWidth: 35 }}>
                    <Typography variant="body2" color="text.secondary">
                      80%
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Last comprehensive review: May 15, 2024
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Regulatory Tracking Table */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Regulation</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Compliance Score</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {regulatoryData.map((reg) => (
                      <TableRow key={reg.id}>
                        <TableCell>{reg.regulation}</TableCell>
                        <TableCell>{reg.category}</TableCell>
                        <TableCell>
                          <ComplianceStatusChip status={reg.status} />
                        </TableCell>
                        <TableCell>{reg.complianceScore}%</TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenDetails(reg)}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Regulation Details Dialog */}
        <Dialog
          open={!!selectedRegulation}
          onClose={handleCloseDetails}
          maxWidth="md"
          fullWidth
        >
          {selectedRegulation && (
            <>
              <DialogTitle>
                {selectedRegulation.regulation} Compliance Details
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1">
                      <strong>Category:</strong> {selectedRegulation.category}
                    </Typography>
                    <Typography variant="subtitle1">
                      <strong>Status:</strong> {selectedRegulation.status}
                    </Typography>
                    <Typography variant="subtitle1">
                      <strong>Last Reviewed:</strong>{" "}
                      {selectedRegulation.lastReviewed}
                    </Typography>
                    <Typography variant="subtitle1">
                      <strong>Next Review Date:</strong>{" "}
                      {selectedRegulation.nextReviewDate}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1">
                      <strong>Compliance Score:</strong>{" "}
                      {selectedRegulation.complianceScore}%
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      {selectedRegulation.details}
                    </Typography>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDetails} color="primary">
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </Layout>
  );
};

export default RegulatoryComplice;
